import {
  BimViewerAdapter,
  BimViewerCallbacks,
  BimViewerHandle,
  BimLoadPhase,
  BimModelSource,
  BimTreeNode,
  BimEntityProperties,
  BimMeasureMode,
  BimSectionPlane
} from 'Data/interfaces/BimViewer/IBimViewerPort';

const VIEWER_VERSION = 'v1.0.0';
const SCRIPT_SRC = `/coordly3DViewer/${VIEWER_VERSION}/initCoordly3DViewer.js`;

// Server ifc-lite: parseia o .ifc e devolve a geometria já tesselada, para o
// browser só decodificar. É o caminho padrão — o parse client-side foi descartado
// (o single-thread do motor perde geometria, e usar o paralelo exigiria
// cross-origin isolation, que quebra a tela de Painéis). Sem a env configurada o
// motor cai no parse local, que só serve para arquivo pequeno.
const IFC_SERVER_URL = process.env.REACT_APP_IFC_SERVER_URL;

interface IfcLiteBridgeConfig {
  fileUrl?: string;
  fileName?: string;
  artifacts?: BimModelSource['artifacts'];
  container?: HTMLElement;
  federated?: boolean;
  serverUrl?: string;
}
interface IfcLiteInstance { dispose(): void; }
type IfcLiteInit = (config: IfcLiteBridgeConfig) => IfcLiteInstance;

// Carrega o bundle uma vez (imutável) via <script src> — não textContent — pros
// imports dinâmicos do bundle resolverem contra a URL do script, não do documento.
const loadEngineScript = (() => {
  let promise: Promise<void> | null = null;
  return () => {
    if ((window as any).initCoordly3DViewer) { return Promise.resolve(); }
    if (promise) { return promise; }
    promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = SCRIPT_SRC;
      script.onload = () => resolve();
      script.onerror = () => { promise = null; reject(new Error('falha ao carregar o motor 3D')); };
      document.head.appendChild(script);
    });
    return promise;
  };
})();

// Adaptador ifc-lite: ÚNICO ponto do web/ que conhece a ponte (window.bim* /
// initCoordly3DViewer / eventos bim-*). Traduz tudo pra porta BimViewerAdapter.
export class IfcLiteViewerAdapter implements BimViewerAdapter {
  async mount(
    container: HTMLElement,
    source: BimModelSource,
    callbacks: BimViewerCallbacks
  ): Promise<BimViewerHandle> {
    const onProgress = (e: Event) =>
      callbacks.onLoadProgress?.((e as CustomEvent).detail?.phase as BimLoadPhase);
    const onLoaded = (e: Event) => callbacks.onLoaded?.((e as CustomEvent).detail);
    const onError = (e: Event) => callbacks.onError?.((e as CustomEvent).detail);
    const onSelection = (e: Event) => callbacks.onSelectionChanged?.((e as CustomEvent).detail);
    const onDataModel = (e: Event) => callbacks.onDataModelReady?.((e as CustomEvent).detail);
    const onMeasure = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      callbacks.onMeasureChanged?.({
        mode: detail?.mode ?? 'none',
        measurements: detail?.measurements ?? [],
      });
    };

    window.addEventListener('bim-measure-changed', onMeasure);
    window.addEventListener('bim-load-progress', onProgress);
    window.addEventListener('bim-file-loaded', onLoaded);
    window.addEventListener('bim-load-error', onError);
    window.addEventListener('bim-selection-changed', onSelection);
    window.addEventListener('bim-datamodel-ready', onDataModel);

    const detach = () => {
      window.removeEventListener('bim-load-progress', onProgress);
      window.removeEventListener('bim-file-loaded', onLoaded);
      window.removeEventListener('bim-load-error', onError);
      window.removeEventListener('bim-selection-changed', onSelection);
      window.removeEventListener('bim-datamodel-ready', onDataModel);
      window.removeEventListener('bim-measure-changed', onMeasure);
    };

    try {
      await loadEngineScript();
    } catch (err) {
      detach();
      throw err;
    }

    const init = (window as any).initCoordly3DViewer as IfcLiteInit;
    const instance = init({
      fileUrl: source.fileUrl,
      fileName: source.fileName,
      artifacts: source.artifacts,
      container,
      serverUrl: IFC_SERVER_URL,
      // Sem fileUrl nem artifacts = cena vazia p/ federação (o app chama addModel).
      federated: !source.fileUrl && !source.artifacts
    });

    // `bimHelpers` é global e trocado a cada mount; ler no momento da chamada
    // (não capturar) mantém o handle apontando pro motor vivo.
    const helpers = () => (window as any).bimHelpers;

    return {
      fitToView: () => helpers()?.fitToView?.(),
      addModel: (s, modelId) =>
        s.fileUrl
          ? helpers()?.addModel?.(s.fileUrl, modelId) ?? Promise.resolve()
          : Promise.resolve(),
      removeModel: (modelId) => helpers()?.removeModel?.(modelId),
      // `true` no fallback: um bundle antigo (sem hasModel) não sabe responder, e
      // assumir "não entrou" faria o app re-adicionar em loop o que já está lá.
      hasModel: (modelId) => helpers()?.hasModel?.(modelId) ?? true,
      clearModels: () => helpers()?.clearModels?.(),
      getSpatialTree: (modelIndex): BimTreeNode[] =>
        helpers()?.getSpatialTree?.(modelIndex) ?? [],
      getEntityProperties: (expressId, modelIndex): BimEntityProperties | null =>
        helpers()?.getEntityProperties?.(expressId, modelIndex) ?? null,
      getEntityLabels: (expressIds, modelIndex) =>
        helpers()?.getEntityLabels?.(expressIds, modelIndex) ?? [],
      select: (expressId, opts) => helpers()?.select?.(expressId, opts),
      clearSelection: () => helpers()?.clearSelection?.(),
      setMultiSelect: (enabled) => helpers()?.setMultiSelect?.(enabled),
      setGhostMode: (enabled) => helpers()?.setGhostMode?.(enabled),
      focusSelection: () => helpers()?.focusSelection?.(),
      frameEntities: (expressIds, modelIndex) =>
        helpers()?.frameEntities?.(expressIds, modelIndex),
      isolate: (expressIds) => helpers()?.isolate?.(expressIds),
      hide: (expressIds) => helpers()?.hide?.(expressIds),
      show: (expressIds) => helpers()?.show?.(expressIds),
      showAll: () => helpers()?.showAll?.(),
      setSectionPlane: (section: BimSectionPlane | null) => helpers()?.setSectionPlane?.(section),
      setMeasureMode: (mode: BimMeasureMode) => helpers()?.setMeasureMode?.(mode),
      deleteMeasurement: (id: string) => helpers()?.deleteMeasurement?.(id),
      clearMeasurements: () => helpers()?.clearMeasurements?.(),
      dispose: () => { detach(); instance.dispose(); }
    };
  }
}
